import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TESTIDS } from '@/constants/testIds';
import { apiPublic, API_BASE } from '@/lib/api';
import SignaturePad from '@/components/SignaturePad';
import PncFooter from '@/components/PncFooter';
import { Check, X } from 'lucide-react';
import { TOOLBOX_TALKS, SITE_RULES as HS_RULES } from '@/data/healthSafetyContent';
import MedicalHavsForm from '@/components/MedicalHavsForm';
import OptOutForm from '@/components/OptOutForm';
function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoToDisplay(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const SERVICE_COPY = {
  invoice: {
    title: 'Weekly Invoice Creation',
    pretitle: 'PNC Service Opt-in',
    agreement: 'I agree for PNC to create my weekly invoices for a £2 weekly charge.',
    summary: 'PNC will prepare and send your weekly invoices on your behalf for a flat charge of £2 per week.',
  },
  insurance: {
    title: 'Insurance Cover',
    pretitle: 'PNC Service Opt-in',
    agreement: 'I agree for my insurance to be covered by PNC for £5 per week.',
    summary: 'PNC will cover your insurance under our scheme for £5 per week.',
  },
};



function ContractSigner({ token, meta }) {
  const [form, setForm] = useState({
    contractor_company: meta.contractor_company || '',
    contractor_name: meta.contractor_name || '',
    contract_date: todayISO(),
    printed_name: '',
    capacity: '',
    signed_date: todayISO(),
  });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const sigRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (sigRef.current.isEmpty()) {
      setError('Please draw your signature before submitting.');
      return;
    }
    if (!agreed) {
      setError('Please confirm your agreement to the contract before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contractor_company: form.contractor_company.trim(),
        contractor_name: form.contractor_name.trim(),
        contract_date: isoToDisplay(form.contract_date),
        printed_name: form.printed_name.trim(),
        capacity: form.capacity.trim(),
        signed_date: isoToDisplay(form.signed_date),
        signature_png: sigRef.current.toDataURL(),
      };
      const r = await apiPublic.post(`/contracts/sign/${token}/submit`, payload);
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit the signed contract.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div data-testid={TESTIDS.signingSuccess} className="max-w-lg w-full pnc-card rounded-lg p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4"><Check size={22} strokeWidth={2.5} /></div>
          <p className="serif text-4xl mb-2">Contract signed</p>
          <p className="text-sm text-[var(--muted)] mb-6">{done.admin_email_sent ? 'A completed copy has been emailed to the admin inbox.' : 'Your contract is signed and stored. The admin email could not be delivered (see admin log) but the signed PDF is saved.'} You can also download your signed copy now.</p>
          <a
            href={`${API_BASE}/contracts/sign/${token}/signed-pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-ink inline-block px-7 py-3 rounded-full text-sm font-semibold"
          >
            Download signed PDF
          </a>
        </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  const pdfUrl = `${API_BASE}/contracts/template?type=${meta.contract_type || 'smart'}`;

  return (
    <div data-testid={TESTIDS.signingPage} className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          <a
            data-testid={TESTIDS.signingDownloadOriginal}
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs underline underline-offset-4 hover:text-white"
          >
            Download contract PDF
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-2 gap-10 relative z-10">
        <section>
          <p className="field-label mb-2">Review</p>
          <h1 className="serif text-3xl mb-4">{meta.contract_type === 'unique' ? 'PNC Unique LTD' : 'PNC Smart Solutions LTD'} &middot; Contract for Services</h1>
          <div className="rounded-lg overflow-hidden border border-[var(--line)] bg-white shadow-sm">
            <iframe
              data-testid={TESTIDS.signingPdfFrame}
              title="Contract preview"
              src={pdfUrl}
              className="w-full"
              style={{ height: '78vh' }}
            />
          </div>
          <p className="text-xs text-[var(--muted)] mt-3">
            Sent to <strong>{meta.employee_email}</strong> &middot; expires {meta.expires_at ? new Date(meta.expires_at).toLocaleDateString() : '—'}.
          </p>
        </section>

        <section>
          <form
            data-testid={TESTIDS.signingForm}
            onSubmit={submit}
            className="pnc-card rounded-lg p-7 space-y-6"
          >
            <div>
              <p className="field-label mb-2">Your details</p>
              <h2 className="serif text-3xl">Sign the contract</h2>
              <p className="text-sm text-[var(--muted)] mt-1">These fields complete the placeholders in the contract.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="field-label block mb-1.5">Contractor company name <span className="text-red-500">*</span></label>
                <input
                  data-testid={TESTIDS.signingContractorCompany}
                  required
                  value={form.contractor_company}
                  onChange={(e) => setForm({ ...form, contractor_company: e.target.value })}
                  className="input-bare"
                  placeholder="e.g. ABC Contracting Ltd"
                />
                <p className="text-[11px] text-[var(--muted)] mt-1">Replaces &quot;COMPANY LTD&quot; in the contract.</p>
              </div>
              <div className="sm:col-span-2">
                <label className="field-label block mb-1.5">Contractor name <span className="text-red-500">*</span></label>
                <input
                  data-testid={TESTIDS.signingContractorName}
                  required
                  value={form.contractor_name}
                  onChange={(e) => setForm({ ...form, contractor_name: e.target.value })}
                  className="input-bare"
                  placeholder="Your full name"
                />
                <p className="text-[11px] text-[var(--muted)] mt-1">Replaces &quot;CONTRACTOR NAME&quot;.</p>
              </div>
              <div>
                <label className="field-label block mb-1.5">Contract date <span className="text-red-500">*</span></label>
                <input
                  data-testid={TESTIDS.signingContractDate}
                  type="date" required
                  value={form.contract_date}
                  onChange={(e) => setForm({ ...form, contract_date: e.target.value })}
                  className="input-bare"
                />
              </div>
            </div>

            <div className="divider-thin" />

            <div className="space-y-4">
              <p className="field-label">Signature panel</p>
              <div>
                <label className="field-label block mb-1.5">Printed name <span className="text-red-500">*</span></label>
                <input
                  data-testid={TESTIDS.signingPrintedName}
                  required
                  value={form.printed_name}
                  onChange={(e) => setForm({ ...form, printed_name: e.target.value })}
                  className="input-bare"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="field-label block mb-1.5">Capacity in which signed <span className="text-red-500">*</span></label>
                  <input
                    data-testid={TESTIDS.signingCapacity}
                    required
                    value={form.capacity}
                    onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                    placeholder="e.g. Director"
                    className="input-bare"
                  />
                </div>
                <div>
                  <label className="field-label block mb-1.5">Date <span className="text-red-500">*</span></label>
                  <input
                    data-testid={TESTIDS.signingSignedDate}
                    type="date" required
                    value={form.signed_date}
                    onChange={(e) => setForm({ ...form, signed_date: e.target.value })}
                    className="input-bare"
                  />
                </div>
              </div>
              <div>
                <label className="field-label block mb-1.5">Signature <span className="text-red-500">*</span></label>
                <SignaturePad ref={sigRef} />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <input
                data-testid={TESTIDS.signingAgree}
                id="agree"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
              />
              <label htmlFor="agree" className="text-sm text-[#D4D4D4] leading-relaxed">
                I confirm I have read and agree to the Contract for Services, and that my electronic
                signature is legally binding.
              </label>
            </div>

            {error && (
              <div data-testid={TESTIDS.signingErrorMsg} className="alert-error text-sm rounded p-3">
                {error}
              </div>
            )}

            <button
              data-testid={TESTIDS.signingSubmit}
              type="submit"
              disabled={submitting}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-semibold"
            >
              {submitting ? 'Submitting your signed contract…' : 'Submit signed contract'}
            </button>
            <p className="text-[11px] text-[var(--muted)] text-center">By submitting you confirm the information above is correct. The signed PDF will be emailed to Admin@pncunique.com.</p>
          </form>
        </section>
      </main>

      <PncFooter />
    </div>
  );
}

function ServiceResponder({ token, meta }) {
  const copy = SERVICE_COPY[meta.service_type] || SERVICE_COPY.invoice;
  const company = meta.contract_type === 'unique' ? 'PNC Unique LTD' : 'PNC Smart Solutions LTD';
  const [choice, setChoice] = useState(null); // 'accepted' | 'declined' | null
  const [workerName, setWorkerName] = useState(meta.contractor_name || '');
  const [workerCompany, setWorkerCompany] = useState(meta.contractor_company || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const sigRef = useRef(null);

  const validateIdentity = () => {
    if (!workerName.trim()) return 'Please enter your full name.';
    if (!workerCompany.trim()) return 'Please enter your company name.';
    return null;
  };

  const submitYes = async () => {
    setError('');
    const idErr = validateIdentity();
    if (idErr) { setError(idErr); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please draw your signature to accept this service.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/respond`, {
        response: 'accepted',
        signature_png: sigRef.current.toDataURL(),
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
      });
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit your response.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDecline = async () => {
    setError('');
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/respond`, {
        response: 'declined',
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
      });
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit your response.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const idErr = validateIdentity();
    if (idErr) { setError(idErr); return; }
    if (choice === 'accepted') submitYes();
    else if (choice === 'declined') setShowConfirm(true);
    else setError('Please choose Yes or No.');
  };

  if (done) {
    const accepted = done.response === 'accepted';
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div
          data-testid={accepted ? TESTIDS.signingSuccess : TESTIDS.serviceResponseDeclined}
          className="max-w-lg w-full pnc-card rounded-lg p-10 text-center"
        >
          <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-2xl mb-4 ${accepted ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
            {accepted ? <Check size={22} strokeWidth={2.5} /> : <X size={22} strokeWidth={2.5} />}
          </div>
          <p className="serif text-4xl mb-2">
            {accepted ? `${copy.title} accepted` : `${copy.title} declined`}
          </p>
          <p className="text-sm text-[var(--muted)] mb-6">
            {accepted
              ? `A signed one-page confirmation has been ${done.admin_email_sent ? 'emailed to the admin inbox and ' : ''}stored.`
              : 'Your decline has been recorded. No further action is required.'}
          </p>
          {accepted && done.signed_pdf_url && (
            <a
              href={`${API_BASE}/contracts/sign/${token}/signed-pdf`}
              target="_blank"
              rel="noreferrer"
              className="btn-ink inline-block px-7 py-3 rounded-full text-sm font-semibold"
            >
              Download confirmation PDF
            </a>
          )}
        </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  return (
    <div data-testid={TESTIDS.signingPage} className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        <p className="field-label mb-2">{copy.pretitle}</p>
        <h1 className="serif text-4xl leading-tight mb-1">{copy.title}</h1>
        <p className="text-sm text-[var(--muted)] mb-1">{company}</p>
        <p className="text-sm text-[var(--muted)] mb-8">
          Sent to <strong>{meta.employee_email}</strong>
          {meta.expires_at ? <> &middot; expires {new Date(meta.expires_at).toLocaleDateString()}</> : null}
        </p>

        <form
          data-testid={TESTIDS.serviceResponseForm}
          onSubmit={handleSubmit}
          className="pnc-card rounded-lg p-7 space-y-6"
        >
          <div className="bg-[var(--paper-deep)] border border-[var(--line)] rounded-md p-5">
            <p className="text-xs uppercase tracking-wider text-[var(--muted)] mb-2">Agreement</p>
            <p className="serif text-xl leading-snug">{copy.agreement}</p>
            <p className="text-sm text-[var(--muted)] mt-3">{copy.summary}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label block mb-1.5">
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                data-testid={TESTIDS.serviceWorkerName}
                value={workerName}
                onChange={(e) => { setWorkerName(e.target.value); setError(''); }}
                placeholder="Your full name"
                className="input-bare"
              />
            </div>
            <div>
              <label className="field-label block mb-1.5">
                Company name <span className="text-red-500">*</span>
              </label>
              <input
                data-testid={TESTIDS.serviceWorkerCompany}
                value={workerCompany}
                onChange={(e) => { setWorkerCompany(e.target.value); setError(''); }}
                placeholder="Your company"
                className="input-bare"
              />
            </div>
          </div>

          <div>
            <p className="field-label block mb-3">Do you agree to opt in?</p>
            <div className="grid grid-cols-2 gap-3">
              <label
                data-testid={TESTIDS.serviceResponseYes}
                className={`flex items-center justify-center gap-2 px-4 py-4 rounded-md border cursor-pointer transition-colors ${
                  choice === 'accepted'
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                    : 'border-[var(--line)] hover:border-[#4A4A4A]'
                }`}
              >
                <input
                  type="radio"
                  name="response"
                  value="accepted"
                  checked={choice === 'accepted'}
                  onChange={() => { setChoice('accepted'); setError(''); }}
                  className="accent-emerald-500"
                />
                <span className="text-base font-semibold">Yes, I agree</span>
              </label>
              <label
                data-testid={TESTIDS.serviceResponseNo}
                className={`flex items-center justify-center gap-2 px-4 py-4 rounded-md border cursor-pointer transition-colors ${
                  choice === 'declined'
                    ? 'border-rose-500 bg-rose-500/10 text-rose-300'
                    : 'border-[var(--line)] hover:border-[#4A4A4A]'
                }`}
              >
                <input
                  type="radio"
                  name="response"
                  value="declined"
                  checked={choice === 'declined'}
                  onChange={() => { setChoice('declined'); setError(''); }}
                  className="accent-rose-500"
                />
                <span className="text-base font-semibold">No, decline</span>
              </label>
            </div>
          </div>

          {choice === 'accepted' && (
            <div className="space-y-2">
              <label className="field-label block">Your signature <span className="text-red-500">*</span></label>
              <SignaturePad ref={sigRef} />
              <p className="text-[11px] text-[var(--muted)]">
                By signing you accept the agreement above. A one-page confirmation PDF will be generated and sent to PNC admin.
              </p>
            </div>
          )}

          {error && (
            <div data-testid={TESTIDS.signingErrorMsg} className="alert-error text-sm rounded p-3">
              {error}
            </div>
          )}

          <button
            data-testid={TESTIDS.serviceResponseSubmit}
            type="submit"
            disabled={submitting || !choice}
            className="btn-ink w-full py-3.5 rounded-md text-sm font-semibold disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : choice === 'declined' ? 'Submit decline' : 'Submit response'}
          </button>
        </form>
      </main>

      <PncFooter />

      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6">
          <div
            data-testid={TESTIDS.serviceResponseDeclineDialog}
            role="dialog"
            aria-modal="true"
            className="bg-[#1E1E1E] border border-[var(--line)] rounded-lg shadow-2xl max-w-md w-full p-7"
          >
            <p className="serif text-2xl mb-2">Are you sure you want to decline this service?</p>
            <p className="text-sm text-[var(--muted)] mb-6">
              Declining will record a &quot;No&quot; response for <strong>{copy.title}</strong>. You can be re-invited later if you change your mind.
            </p>
            <div className="flex justify-end gap-2">
              <button
                data-testid={TESTIDS.serviceResponseDeclineCancel}
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                className="btn-ghost px-4 py-2 rounded-md text-sm font-semibold"
                type="button"
              >
                Go back
              </button>
              <button
                data-testid={TESTIDS.serviceResponseDeclineConfirm}
                onClick={submitDecline}
                disabled={submitting}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                type="button"
              >
                {submitting ? 'Submitting…' : 'Yes, decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HealthSafetyResponder({ token, meta }) {
  const company = meta.contract_type === 'unique' ? 'PNC Unique LTD' : 'PNC Smart Solutions LTD';
  const [step, setStep] = useState('toolbox'); // 'toolbox' → 'rules' → 'decision'
  const [acknowledged, setAcknowledged] = useState(() => new Set());
  const [readChecked, setReadChecked] = useState(false);
  const [choice, setChoice] = useState(null);
  const [workerName, setWorkerName] = useState(meta.contractor_name || '');
  const [workerCompany, setWorkerCompany] = useState(meta.contractor_company || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const sigRef = useRef(null);

  const validateIdentity = () => {
    if (!workerName.trim()) return 'Please enter your full name.';
    if (!workerCompany.trim()) return 'Please enter your company name.';
    return null;
  };

  const markCompliant = (idx) => {
    setAcknowledged((prev) => {
      if (prev.has(idx)) return prev;
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
    setError('');
  };

  const allToolboxDone = acknowledged.size === TOOLBOX_TALKS.length;

  const goToRules = () => {
    if (!allToolboxDone) {
      setError(`Please mark all ${TOOLBOX_TALKS.length} sections as compliant before continuing.`);
      return;
    }
    setError('');
    setStep('rules');
  };

  const goCompliant = () => {
    if (!readChecked) {
      setError('Please tick the box to confirm you have read the Site Rules.');
      return;
    }
    setError('');
    setStep('decision');
  };

  const submitAccept = async () => {
    setError('');
    const idErr = validateIdentity();
    if (idErr) { setError(idErr); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please draw your signature to accept.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/respond`, {
        response: 'accepted',
        signature_png: sigRef.current.toDataURL(),
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
        acknowledgements: TOOLBOX_TALKS.map((t) => t.title),
      });
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit your response.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitDecline = async () => {
    setError('');
    setShowConfirm(false);
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/respond`, {
        response: 'declined',
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
      });
      setDone(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to submit your response.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const idErr = validateIdentity();
    if (idErr) { setError(idErr); return; }
    if (choice === 'accepted') submitAccept();
    else if (choice === 'declined') setShowConfirm(true);
    else setError('Please choose Accept or Decline.');
  };

  if (done) {
    const accepted = done.response === 'accepted';
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div
          data-testid={accepted ? TESTIDS.signingSuccess : TESTIDS.serviceResponseDeclined}
          className="max-w-lg w-full pnc-card rounded-lg p-10 text-center"
        >
          <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center text-2xl mb-4 ${accepted ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400'}`}>
            {accepted ? <Check size={22} strokeWidth={2.5} /> : <X size={22} strokeWidth={2.5} />}
          </div>
          <p className="serif text-4xl mb-2">
            {accepted ? 'Health & Safety acknowledged' : 'Health & Safety declined'}
          </p>
          <p className="text-sm text-[var(--muted)] mb-6">
            {accepted
              ? `Your acknowledgement has been recorded${done.admin_email_sent ? ' and emailed to PNC admin' : ''}.`
              : 'Your decline has been recorded. No further action is required.'}
          </p>
          {accepted && done.signed_pdf_url && (
            <a
              href={`${API_BASE}/contracts/sign/${token}/signed-pdf`}
              target="_blank"
              rel="noreferrer"
              className="btn-ink inline-block px-7 py-3 rounded-full text-sm font-semibold"
            >
              Download confirmation PDF
            </a>
          )}
        </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  return (
    <div data-testid={TESTIDS.signingPage} className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 relative z-10">
        <p className="field-label mb-2">Health &amp; Safety Acknowledgement</p>
        <h1 className="serif text-4xl leading-tight mb-1">
          {step === 'toolbox' ? 'Tool Box Talks' : 'Site Rules'}
        </h1>
        <p className="text-sm text-[var(--muted)] mb-1">{company}</p>
        <p className="text-sm text-[var(--muted)] mb-8">
          Sent to <strong>{meta.employee_email}</strong>
          {meta.expires_at ? <> &middot; expires {new Date(meta.expires_at).toLocaleDateString()}</> : null}
        </p>

        {step === 'toolbox' && (
          <form
            data-testid={TESTIDS.serviceResponseForm}
            onSubmit={(e) => { e.preventDefault(); goToRules(); }}
            className="space-y-6"
          >
            <div className="pnc-card rounded-lg p-6 flex items-start justify-between gap-4">
              <div>
                <p className="serif text-2xl leading-snug mb-1">Tool Box Talks – Health &amp; Safety</p>
                <p className="text-sm text-[var(--muted)]">
                  Read each section below and press <strong>Mark as Compliant</strong> to confirm you have
                  understood the information. You must acknowledge all {TOOLBOX_TALKS.length} sections
                  before you can continue.
                </p>
              </div>
              <div
                data-testid={TESTIDS.hsToolboxCounter}
                className={`shrink-0 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md border ${
                  allToolboxDone
                    ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                    : 'border-[var(--line)] bg-[var(--paper-deep)] text-[#D4D4D4]'
                }`}
              >
                {acknowledged.size} of {TOOLBOX_TALKS.length}<br />acknowledged
              </div>
            </div>

            <ol data-testid={TESTIDS.hsToolboxList} className="space-y-4">
              {TOOLBOX_TALKS.map((section, idx) => {
                const isAck = acknowledged.has(idx);
                return (
                  <li
                    key={idx}
                    data-testid={TESTIDS.hsToolboxCard}
                    data-section-index={idx}
                    className={`bg-[#1E1E1E] border rounded-lg overflow-hidden transition-colors ${
                      isAck ? 'border-emerald-500/70' : 'border-[var(--line)]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-[var(--line)] bg-[var(--paper-deep)]">
                      <div className="flex items-baseline gap-3">
                        <span className="text-xs font-bold text-[var(--muted)]">{String(idx + 1).padStart(2, '0')}</span>
                        <h2 className="serif text-xl leading-snug">{section.title}</h2>
                      </div>
                      {isAck && (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider bg-emerald-600 text-white px-2.5 py-1 rounded-full">
                          <Check size={11} strokeWidth={3} /> Acknowledged
                        </span>
                      )}
                    </div>
                    <div className="px-6 py-5 space-y-3 text-sm leading-relaxed text-[#E5E5E5]">
                      {section.body.map((blk, j) => {
                        if (blk.h) {
                          return (
                            <p key={j} className="font-semibold text-white pt-1">
                              {blk.h}
                            </p>
                          );
                        }
                        if (blk.p) {
                          return <p key={j}>{blk.p}</p>;
                        }
                        if (blk.list) {
                          return (
                            <ul key={j} className="list-disc pl-5 space-y-1">
                              {blk.list.map((li, k) => <li key={k}>{li}</li>)}
                            </ul>
                          );
                        }
                        return null;
                      })}
                      <p className="text-[12px] text-[var(--muted)] pt-2 border-t border-[var(--line)] mt-3">
                        Every employee/contractor must take care of their own health and safety
                        and that of others who may be affected by their actions. Please read
                        the section above, then mark it as compliant.
                      </p>
                    </div>
                    <div className="px-6 py-4 bg-[#1E1E1E] border-t border-[var(--line)] flex justify-end">
                      <button
                        type="button"
                        data-testid={TESTIDS.hsToolboxMarkBtn}
                        data-section-index={idx}
                        onClick={() => markCompliant(idx)}
                        disabled={isAck}
                        className={`px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                          isAck
                            ? 'bg-emerald-600 text-white cursor-default'
                            : 'btn-ink'
                        }`}
                      >
                        {isAck ? 'Marked as Compliant' : 'Mark as Compliant'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>

            {error && (
              <div data-testid={TESTIDS.signingErrorMsg} className="alert-error text-sm rounded p-3">
                {error}
              </div>
            )}

            <button
              data-testid={TESTIDS.hsToolboxContinue}
              type="submit"
              disabled={!allToolboxDone}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-semibold disabled:opacity-50"
            >
              {allToolboxDone
                ? 'Continue to Site Rules'
                : `Acknowledge all ${TOOLBOX_TALKS.length} sections to continue`}
            </button>
          </form>
        )}

        {step === 'rules' && (
          <form
            data-testid={TESTIDS.serviceResponseForm}
            onSubmit={(e) => { e.preventDefault(); goCompliant(); }}
            className="pnc-card rounded-lg p-7 space-y-6"
          >
            <p className="text-sm text-[#D4D4D4] leading-relaxed">
              Please read the following site rules carefully. You must press
              <strong> Compliant </strong> at the bottom to confirm before you can continue.
            </p>

            <ol data-testid={TESTIDS.hsRulesList} className="space-y-3 list-decimal pl-6 text-sm leading-relaxed text-[#E5E5E5]">
              {HS_RULES.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ol>

            <div className="flex items-start gap-3 pt-1 border-t border-[var(--line)] pt-5">
              <input
                data-testid={TESTIDS.hsReadCheckbox}
                id="hs-read"
                type="checkbox"
                checked={readChecked}
                onChange={(e) => { setReadChecked(e.target.checked); setError(''); }}
                className="mt-1"
              />
              <label htmlFor="hs-read" className="text-sm text-[#E5E5E5] leading-relaxed font-semibold">
                I have read and understood the Site Rules.
              </label>
            </div>

            {error && (
              <div data-testid={TESTIDS.signingErrorMsg} className="alert-error text-sm rounded p-3">
                {error}
              </div>
            )}

            <button
              data-testid={TESTIDS.hsCompliantBtn}
              type="submit"
              disabled={!readChecked}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-semibold disabled:opacity-50"
            >
              Compliant – I have read &amp; understood
            </button>
          </form>
        )}

        {step === 'decision' && (
          <form
            data-testid={TESTIDS.serviceResponseForm}
            onSubmit={handleSubmit}
            className="pnc-card rounded-lg p-7 space-y-6"
          >
            <div className="bg-[var(--paper-deep)] border border-[var(--line)] rounded-md p-5">
              <p className="text-xs uppercase tracking-wider text-[var(--muted)] mb-2">Acceptance statement</p>
              <p className="serif text-xl leading-snug">
                I have read and understood the Site Rules and confirm I will comply with all health &amp; safety requirements while on site.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="field-label block mb-1.5">
                  Full name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid={TESTIDS.hsWorkerName}
                  value={workerName}
                  onChange={(e) => { setWorkerName(e.target.value); setError(''); }}
                  placeholder="Your full name"
                  className="input-bare"
                />
              </div>
              <div>
                <label className="field-label block mb-1.5">
                  Company name <span className="text-red-500">*</span>
                </label>
                <input
                  data-testid={TESTIDS.hsWorkerCompany}
                  value={workerCompany}
                  onChange={(e) => { setWorkerCompany(e.target.value); setError(''); }}
                  placeholder="Your company"
                  className="input-bare"
                />
              </div>
            </div>

            <div>
              <p className="field-label block mb-3">Do you accept the Site Rules?</p>
              <div className="grid grid-cols-2 gap-3">
                <label
                  data-testid={TESTIDS.serviceResponseYes}
                  className={`flex items-center justify-center gap-2 px-4 py-4 rounded-md border cursor-pointer transition-colors ${
                    choice === 'accepted'
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300'
                      : 'border-[var(--line)] hover:border-[#4A4A4A]'
                  }`}
                >
                  <input
                    type="radio"
                    name="response"
                    value="accepted"
                    checked={choice === 'accepted'}
                    onChange={() => { setChoice('accepted'); setError(''); }}
                    className="accent-emerald-500"
                  />
                  <span className="text-base font-semibold">Accept</span>
                </label>
                <label
                  data-testid={TESTIDS.serviceResponseNo}
                  className={`flex items-center justify-center gap-2 px-4 py-4 rounded-md border cursor-pointer transition-colors ${
                    choice === 'declined'
                      ? 'border-rose-500 bg-rose-500/10 text-rose-300'
                      : 'border-[var(--line)] hover:border-[#4A4A4A]'
                  }`}
                >
                  <input
                    type="radio"
                    name="response"
                    value="declined"
                    checked={choice === 'declined'}
                    onChange={() => { setChoice('declined'); setError(''); }}
                    className="accent-rose-500"
                  />
                  <span className="text-base font-semibold">Decline</span>
                </label>
              </div>
            </div>

            {choice === 'accepted' && (
              <div className="space-y-2">
                <label className="field-label block">Your signature <span className="text-red-500">*</span></label>
                <SignaturePad ref={sigRef} />
                <p className="text-[11px] text-[var(--muted)]">
                  A one-page Health &amp; Safety confirmation PDF will be generated, stamped with your IP and timestamp, and sent to PNC admin.
                </p>
              </div>
            )}

            {error && (
              <div data-testid={TESTIDS.signingErrorMsg} className="alert-error text-sm rounded p-3">
                {error}
              </div>
            )}

            <button
              data-testid={TESTIDS.serviceResponseSubmit}
              type="submit"
              disabled={submitting || !choice}
              className="btn-ink w-full py-3.5 rounded-md text-sm font-semibold disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : choice === 'declined' ? 'Submit decline' : 'Submit response'}
            </button>
          </form>
        )}
      </main>

      <PncFooter />

      {showConfirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-6">
          <div
            data-testid={TESTIDS.serviceResponseDeclineDialog}
            role="dialog"
            aria-modal="true"
            className="bg-[#1E1E1E] border border-[var(--line)] rounded-lg shadow-2xl max-w-md w-full p-7"
          >
            <p className="serif text-2xl mb-2">Are you sure you want to decline this service?</p>
            <p className="text-sm text-[var(--muted)] mb-6">
              Declining will record a &quot;No&quot; response for the <strong>Health &amp; Safety Form</strong>. You can be re-invited later if you change your mind.
            </p>
            <div className="flex justify-end gap-2">
              <button
                data-testid={TESTIDS.serviceResponseDeclineCancel}
                onClick={() => setShowConfirm(false)}
                disabled={submitting}
                className="btn-ghost px-4 py-2 rounded-md text-sm font-semibold"
                type="button"
              >
                Go back
              </button>
              <button
                data-testid={TESTIDS.serviceResponseDeclineConfirm}
                onClick={submitDecline}
                disabled={submitting}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-600 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                type="button"
              >
                {submitting ? 'Submitting…' : 'Yes, decline'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentSigner({ token, meta }) {
  const sigRef = useRef(null);
  const [workerName, setWorkerName] = useState('');
  const [workerCompany, setWorkerCompany] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const documentName = meta?.document_name || 'Document for Signature';

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError('Please draw your signature before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await apiPublic.post(`/contracts/sign/${token}/submit-document`, {
        worker_name: workerName.trim(),
        worker_company: workerCompany.trim(),
        signature_png: sigRef.current.toDataURL(),
      });
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div data-testid={TESTIDS.signingSuccess} className="max-w-lg w-full pnc-card rounded-lg p-10 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mb-4"><Check size={22} strokeWidth={2.5} /></div>
          <h1 className="serif text-3xl mb-2">Document signed</h1>
          <p className="text-sm text-[var(--muted)] mb-6">
            Thank you, {workerName}. <strong className="text-[var(--text)]">{documentName}</strong> has
            been signed and delivered to PNC. A copy is stored securely for your records.
          </p>
          <a
            data-testid={TESTIDS.signingDownloadSigned}
            href={`${API_BASE}/contracts/sign/${token}/signed-pdf`}
            target="_blank"
            rel="noreferrer"
            className="btn-ink inline-block px-6 py-2.5 rounded-md text-sm font-semibold"
          >
            Download signed PDF
          </a>
        </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen grain">
      <header className="relative border-b border-[var(--line)] bg-[#0A0A0A]/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/pnc-logo-white.png" alt="PNC UNIQUE LTD" className="h-9 w-auto" />
          <span className="text-xs text-[var(--muted)]">Secure signing link</span>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="field-label mb-3">Document for signature</p>
          <h1 className="serif text-3xl sm:text-4xl leading-tight mb-2">{documentName}</h1>
          <p className="text-sm text-[var(--muted)]">
            Sent to <strong className="text-[var(--text)]">{meta?.employee_email}</strong>. Please read the
            document below in full, then sign at the bottom.
          </p>
        </div>

        <div className="rounded-lg overflow-hidden border border-[var(--line)] bg-white shadow-sm mb-8" style={{ height: '65vh' }}>
          <iframe
            data-testid="document-signer-iframe"
            title={documentName}
            src={`${API_BASE}/contracts/sign/${token}/document`}
            className="w-full h-full"
          />
        </div>

        <form data-testid={TESTIDS.documentSignerForm} onSubmit={submit} className="pnc-card rounded-lg p-7 space-y-5">
          <div>
            <p className="field-label mb-1">Sign the document</p>
            <p className="text-xs text-[var(--muted)]">Your signature, date, time and IP address will be recorded on an audit page appended to the document.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label block mb-1.5">Full name</label>
              <input data-testid={TESTIDS.docSignName} value={workerName}
                     onChange={(e) => setWorkerName(e.target.value)}
                     className="input-bare" placeholder="Your full legal name" required maxLength={200} />
            </div>
            <div>
              <label className="field-label block mb-1.5">Company name</label>
              <input data-testid={TESTIDS.docSignCompany} value={workerCompany}
                     onChange={(e) => setWorkerCompany(e.target.value)}
                     className="input-bare" placeholder="Your company / trading name" required maxLength={200} />
            </div>
          </div>
          <div>
            <label className="field-label block mb-1.5">Signature</label>
            <SignaturePad ref={sigRef} />
          </div>
          <label className="flex items-start gap-2.5 text-sm text-[#D4D4D4] cursor-pointer">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                   className="accent-[var(--silver)] mt-0.5" required />
            <span>I confirm I have read and agree to <strong>{documentName}</strong> in full, and I consent to signing it electronically.</span>
          </label>
          {error && <div className="alert-error text-sm rounded p-3">{error}</div>}
          <button
            data-testid={TESTIDS.docSignSubmit}
            type="submit"
            disabled={submitting || !agree || !workerName.trim() || !workerCompany.trim()}
            className="btn-ink w-full py-3 rounded-md text-sm font-bold uppercase tracking-[0.08em]"
          >
            {submitting ? 'Submitting…' : 'Sign & submit document'}
          </button>
        </form>
      </main>

      <PncFooter />
    </div>
  );
}

export default function SigningPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    apiPublic.get(`/contracts/sign/${token}`)
      .then((r) => setState({ loading: false, meta: r.data }))
      .catch((e) => setState({ loading: false, error: e.response?.data?.detail || 'Could not load the contract.' }));
  }, [token]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--muted)]">Loading…</div>;
  }
  if (state.error) {
    return (
      <div className="min-h-screen grain flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div data-testid={TESTIDS.signingErrorState} className="max-w-md text-center pnc-card rounded-lg p-10">
          <p className="serif text-3xl mb-2">Unable to open link</p>
          <p className="text-sm text-[var(--muted)]">{state.error}</p>
          <p className="text-xs text-[#8A8A8A] mt-6">If you believe this is a mistake, please contact <a className="underline" href="mailto:Admin@pncunique.com">Admin@pncunique.com</a>.</p>
        </div>
        </div>
        <PncFooter />
      </div>
    );
  }

  const serviceType = state.meta?.service_type || 'contract';
  if (serviceType === 'contract') {
    return <ContractSigner token={token} meta={state.meta} />;
  }
  if (serviceType === 'custom') {
    return <DocumentSigner token={token} meta={state.meta} />;
  }
  if (serviceType === 'health_safety') {
    return <HealthSafetyResponder token={token} meta={state.meta} />;
  }
  if (serviceType === 'medical') {
    return <MedicalHavsForm token={token} meta={state.meta} />;
  }
  if (serviceType === 'optout_48h') {
    return <OptOutForm token={token} meta={state.meta} />;
  }
  return <ServiceResponder token={token} meta={state.meta} />;
}
